import { authFetch, buildWorkspaceUrl, throwApiError } from './core'
import type { MemberRole } from './models'

export interface BackendUserInfo {
  sub: string
  name?: string
  email?: string
  picture?: string
  email_verified?: boolean
  fullname?: string
  firstName?: string
  lastName?: string
  avatar?: string
  logoFileId?: string
  roles?: string[]
  enabled?: boolean
  [key: string]: unknown
}

export interface WorkspaceInfo {
  id: string
  name: string
  domain: string
  [key: string]: unknown
}

export interface MemberInfo {
  id: string
  userId: string
  name?: string
  username?: string
  type: string
  role: MemberRole
  [key: string]: unknown
}

export interface WorkspaceSessionUser {
  workspaceId: string
  userId: string | null
  name: string | null
  username: string | null
  role: MemberRole | null
}

let cachedWorkspaceId: string | null = null
let cachedMemberType: string | null = null
let cachedMemberRole: MemberRole | null = null
let cachedMemberName: string | null = null
let cachedMemberUsername: string | null = null
let cachedMemberUserId: string | null = null
let initPromise: Promise<void> | null = null

function resolveExtraHeaders(
  tokenOrExtra?: string | Record<string, string>,
  maybeExtra?: Record<string, string>,
): Record<string, string> {
  if (typeof tokenOrExtra === 'string' || tokenOrExtra == null) {
    return maybeExtra ?? {}
  }

  return tokenOrExtra
}

export function getWorkspaceId(): string | null {
  return cachedWorkspaceId
}

export function getMemberType(): string | null {
  return cachedMemberType
}

export function getMemberRole(): MemberRole | null {
  return cachedMemberRole
}

export function getMemberName(): string | null {
  return cachedMemberName
}

export function getMemberUsername(): string | null {
  return cachedMemberUsername
}

export function getMemberUserId(): string | null {
  return cachedMemberUserId
}

export function requestOptions(
  tokenOrExtra?: string | Record<string, string>,
  extra?: Record<string, string>,
): RequestInit {
  const resolvedExtra = resolveExtraHeaders(tokenOrExtra, extra)
  const headers: Record<string, string> = {
    ...resolvedExtra,
  }

  if (cachedWorkspaceId) {
    headers['x-workspace-id'] = cachedWorkspaceId
  }

  if (cachedMemberRole) {
    headers['x-user-workspace-roles'] = cachedMemberRole
  }

  return {
    credentials: 'include',
    headers,
  }
}

export async function fetchBackendUserInfo(_token?: string): Promise<BackendUserInfo> {
  const response = await authFetch('/api/userinfo')
  if (!response.ok) {
    await throwApiError(response, 'Failed to fetch userinfo')
  }
  return response.json() as Promise<BackendUserInfo>
}

async function fetchWorkspaces(_token?: string): Promise<WorkspaceInfo[]> {
  const response = await authFetch(
    buildWorkspaceUrl('/api/workspace/users/workspaces'),
    requestOptions(),
  )
  if (!response.ok) {
    await throwApiError(response, 'Failed to fetch workspaces')
  }
  return response.json() as Promise<WorkspaceInfo[]>
}

async function fetchMemberInfo(_token?: string): Promise<MemberInfo> {
  const response = await authFetch(
    buildWorkspaceUrl('/api/workspace/users/workspaces/me/member'),
    requestOptions(),
  )
  if (!response.ok) {
    await throwApiError(response, 'Failed to fetch member info')
  }
  return response.json() as Promise<MemberInfo>
}

export async function refreshMemberInfo(token?: string): Promise<MemberInfo> {
  const member = await fetchMemberInfo(token)
  cachedMemberType = member.type
  cachedMemberRole = member.role ?? null
  cachedMemberName = member.name ?? null
  cachedMemberUsername = member.username ?? null
  cachedMemberUserId = member.userId ?? null
  return member
}

export function clearWorkspaceCache(): void {
  cachedWorkspaceId = null
  cachedMemberType = null
  cachedMemberRole = null
  cachedMemberName = null
  cachedMemberUsername = null
  cachedMemberUserId = null
  initPromise = null
}

export function initWorkspace(token?: string): Promise<void> {
  if (cachedWorkspaceId !== null) return Promise.resolve()
  if (initPromise) return initPromise

  initPromise = (async () => {
    const workspaces = await fetchWorkspaces(token)
    if (workspaces.length === 0) {
      throw new Error('No workspaces available for this user')
    }

    cachedWorkspaceId = workspaces[0].id

    const member = await fetchMemberInfo(token)
    cachedMemberType = member.type
    cachedMemberRole = member.role ?? null
    cachedMemberName = member.name ?? null
    cachedMemberUsername = member.username ?? null
    cachedMemberUserId = member.userId ?? null
  })().finally(() => {
    initPromise = null
  })

  return initPromise
}

export async function bootstrapWorkspaceSession(token?: string): Promise<WorkspaceSessionUser> {
  await initWorkspace(token)

  if (!cachedWorkspaceId) {
    throw new Error('Workspace session bootstrap completed without a workspace id')
  }

  return {
    workspaceId: cachedWorkspaceId,
    userId: cachedMemberUserId,
    name: cachedMemberName,
    username: cachedMemberUsername,
    role: cachedMemberRole,
  }
}
